import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, prettyDate, money } from "@/lib/api";
import { Panel } from "@/components/admin/Panel";
import { BookingDetail } from "@/components/admin/BookingDetail";
import { StatusPill, displayStatus, matchesSearch } from "@/components/admin/status";

const STATUSES = ["all", "upcoming", "pending", "confirmed", "completed", "cancelled", "expired"];

export const BookingsManager = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("upcoming");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/bookings").then((r) => setItems(r.data)).catch(() => toast.error("Could not load reservations"));
  useEffect(() => { load(); }, []);
  const shown = (items || []).filter((b) => {
    const ds = displayStatus(b);
    if (status === "upcoming" ? !["confirmed", "pending"].includes(ds) : status !== "all" && ds !== status) return false;
    if (from && b.check_out < from) return false;
    if (to && b.check_in > to) return false;
    return matchesSearch(b, q);
  });
  return (
    <Panel title="Reservations" desc="Search, filter and open any reservation. Test-mode bookings are labeled and never counted as real revenue." testid="reservations-panel">
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <Input placeholder="Search ref, name, email, phone" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="res-search-input" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-input rounded-md h-10 px-3 text-sm bg-white" data-testid="res-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <label className="text-xs">Stays from<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="res-from-date" /></label>
        <label className="text-xs">to<Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="res-to-date" /></label>
      </div>
      {!items ? <p className="text-sm text-muted-foreground">Loading…</p> : !shown.length ? <p className="text-sm text-muted-foreground" data-testid="reservations-empty">No reservations match.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Dates</TableHead><TableHead>Guest</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((b) => (
                <TableRow key={b.id} onClick={() => setOpen(b)} className="cursor-pointer" data-testid={`reservation-row-${b.ref}`}>
                  <TableCell className="font-medium">{b.ref}{b.test_mode && <span className="block text-[10px] uppercase text-[hsl(30_60%_35%)]">Test</span>}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{prettyDate(b.check_in)}<br />→ {prettyDate(b.check_out)} · {b.nights}n</TableCell>
                  <TableCell className="text-sm">{b.name}<br /><span className="text-muted-foreground">{b.email}</span></TableCell>
                  <TableCell>{money(b.total)}</TableCell>
                  <TableCell><StatusPill value={displayStatus(b)} testid={`reservation-status-${b.ref}`} /></TableCell>
                  <TableCell><StatusPill value={b.payment_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <BookingDetail booking={open} onClose={() => setOpen(null)} onChanged={(close = true) => { if (close) setOpen(null); load(); }} />
    </Panel>
  );
};
